import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-neutral-500 text-primary-foreground [a&]:hover:bg-primary/90",
        primary:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        success:
          "border-transparent bg-success text-black [a&]:hover:bg-success/90",
        warning:
          "border-transparent bg-warning text-warning-foreground [a&]:hover:bg-warning/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        lime:
          "border-transparent bg-accent-lime text-accent-lime-foreground [a&]:hover:bg-accent-lime/80",
        lilac:
          "border-transparent bg-accent-lilac text-accent-lilac-foreground [a&]:hover:bg-accent-lilac/80",
        orange:
          "border-transparent bg-accent-orange text-accent-orange-foreground [a&]:hover:bg-accent-orange/80",
        "hot-pink":
          "border-transparent bg-accent-hot-pink text-accent-hot-pink-foreground [a&]:hover:bg-accent-hot-pink/80",
      },
      tint: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        tint: true,
        className:
          "bg-neutral-100 text-neutral-700 dark:text-neutral-300 [a&]:hover:bg-neutral-500/20",
      },
      {
        variant: "primary",
        tint: true,
        className:
          "bg-primary-50 text-primary dark:text-primary [a&]:hover:bg-primary/20",
      },
      {
        variant: "secondary",
        tint: true,
        className:
          "bg-secondary-50 text-secondary [a&]:hover:bg-secondary/20",
      },
      {
        variant: "success",
        tint: true,
        className:
          "bg-success-100 text-success-700 dark:text-success [a&]:hover:bg-success/20",
      },
      {
        variant: "warning",
        tint: true,
        className:
          "bg-warning-100 text-warning-800 [a&]:hover:bg-warning/20",
      },
      {
        variant: "destructive",
        tint: true,
        className:
          "bg-destructive-50 text-destructive dark:text-destructive [a&]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
      },
      {
        variant: "lime",
        tint: true,
        className:
          "bg-accent-lime/30 text-accent-lime-foreground [a&]:hover:bg-accent-lime/50",
      },
      {
        variant: "lilac",
        tint: true,
        className:
          "bg-accent-lilac/30 text-accent-lilac-foreground [a&]:hover:bg-accent-lilac/50",
      },
      {
        variant: "orange",
        tint: true,
        className:
          "bg-accent-orange/20 text-accent-orange [a&]:hover:bg-accent-orange/30",
      },
      {
        variant: "hot-pink",
        tint: true,
        className:
          "bg-accent-hot-pink/15 text-accent-hot-pink [a&]:hover:bg-accent-hot-pink/25",
      },
    ],
    defaultVariants: {
      variant: "default",
      tint: false,
    },
  }
)

function Badge({ className, variant, tint, asChild = false, ...props }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, tint }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
